import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, Users, CalendarDays } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { FORMULES } from "./Packages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["Mariage", "Soirée privée", "Séminaire", "Anniversaire", "Autre"];

export default function QuoteForm() {
  const [formuleIdx, setFormuleIdx] = useState(1);
  const [invites, setInvites] = useState(120);
  const [type, setType] = useState("Mariage");
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", date: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = (e) => setFormuleIdx(e.detail);
    window.addEventListener("klmprod:formule", handler);
    return () => window.removeEventListener("klmprod:formule", handler);
  }, []);

  const formule = FORMULES[formuleIdx];

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/devis`, {
        nom: form.nom,
        email: form.email,
        telephone: form.telephone || null,
        date_evenement: form.date || null,
        type_evenement: type,
        formule: formule.name,
        invites,
        message: form.message || null,
      });
      toast.success("Demande de devis envoyée", {
        description: "Merci ! Nous revenons vers vous sous 48 h avec une proposition détaillée.",
      });
      setForm({ nom: "", email: "", telephone: "", date: "", message: "" });
      setType("Mariage");
      setInvites(120);
    } catch (err) {
      toast.error("L'envoi a échoué", {
        description: "Vérifiez les champs du formulaire et réessayez.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="devis" className="relative bg-[#0A101B] py-28 sm:py-36" data-testid="quote-form-section">
      <div
        className="absolute left-1/2 top-24 h-[380px] w-[760px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(closest-side, rgba(0,140,255,0.12), transparent)" }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          testId="quote-form-heading"
          eyebrow="Parlons de votre événement"
          title={
            <>
              Votre devis <span className="text-gradient">en quelques minutes.</span>
            </>
          }
          text="Ajustez les curseurs, laissez-nous vos coordonnées : nous revenons vers vous sous 48 h avec une proposition détaillée."
        />

        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <form
              onSubmit={submit}
              className="space-y-8 rounded-3xl border border-cyan-500/15 bg-[#0E1524]/80 p-7 backdrop-blur-xl sm:p-10"
              data-testid="devis-form"
            >
              <div>
                <div className="flex items-baseline justify-between">
                  <Label className="text-sm font-semibold text-slate-200">Votre formule</Label>
                  <span className="font-mono-brand text-xs uppercase tracking-widest text-cyan-400" data-testid="devis-formule-label">
                    {formule.name}
                  </span>
                </div>
                <Slider
                  data-testid="devis-formule-slider"
                  min={0}
                  max={2}
                  step={1}
                  value={[formuleIdx]}
                  onValueChange={([v]) => setFormuleIdx(v)}
                  className="mt-5"
                />
                <div className="mt-2 flex justify-between text-xs text-slate-500">
                  {FORMULES.map((f) => (
                    <span key={f.name} className={f.name === formule.name ? "font-semibold text-cyan-300" : ""}>
                      {f.name}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-baseline justify-between">
                  <Label className="text-sm font-semibold text-slate-200">Nombre d'invités prévus</Label>
                  <span className="flex items-center gap-1.5 font-mono-brand text-xs tracking-widest text-cyan-400" data-testid="devis-invites-label">
                    <Users size={13} />
                    {invites >= 300 ? "300+" : invites}
                  </span>
                </div>
                <Slider
                  data-testid="devis-invites-slider"
                  min={20}
                  max={300}
                  step={10}
                  value={[invites]}
                  onValueChange={([v]) => setInvites(v)}
                  className="mt-5"
                />
                <div className="mt-2 flex justify-between text-xs text-slate-500">
                  <span>20</span>
                  <span>300+</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="devis-nom" className="text-sm text-slate-300">Nom & prénom *</Label>
                  <Input
                    id="devis-nom"
                    data-testid="devis-nom-input"
                    required
                    value={form.nom}
                    onChange={set("nom")}
                    placeholder="Camille & Alex Martin"
                    className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="devis-email" className="text-sm text-slate-300">E-mail *</Label>
                  <Input
                    id="devis-email"
                    data-testid="devis-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="vous@exemple.fr"
                    className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="devis-tel" className="text-sm text-slate-300">Téléphone</Label>
                  <Input
                    id="devis-tel"
                    data-testid="devis-telephone-input"
                    type="tel"
                    value={form.telephone}
                    onChange={set("telephone")}
                    placeholder="06 12 34 56 78"
                    className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="devis-date" className="flex items-center gap-1.5 text-sm text-slate-300">
                    <CalendarDays size={14} className="text-cyan-400" /> Date de l'événement
                  </Label>
                  <Input
                    id="devis-date"
                    data-testid="devis-date-input"
                    type="date"
                    value={form.date}
                    onChange={set("date")}
                    className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 focus-visible:ring-cyan-400/50"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-sm text-slate-300">Type d'événement</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger
                      data-testid="devis-type-select"
                      className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 focus:ring-cyan-400/50"
                    >
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent className="border-cyan-500/20 bg-[#0E1524] text-slate-100">
                      {EVENT_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`devis-type-option-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="devis-message" className="text-sm text-slate-300">Votre projet, en quelques mots</Label>
                  <Textarea
                    id="devis-message"
                    data-testid="devis-message-textarea"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Le lieu, l'ambiance souhaitée, une surprise prévue…"
                    className="border-cyan-500/20 bg-[#080C14]/70 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400/50"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="devis-submit-button"
                disabled={sending}
                className="bg-brand-gradient flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-bold text-[#04121f] shadow-[0_0_40px_rgba(0,150,255,0.25)] transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
              >
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Envoi en cours…" : "Envoyer ma demande de devis"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2">
            <aside
              className="sticky top-24 rounded-3xl border border-cyan-400/25 bg-gradient-to-b from-[#0E1524] to-[#0B1E33] p-8"
              data-testid="devis-estimate"
            >
              <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">Votre estimation</p>
              <p className="font-display mt-5 text-xl font-bold text-slate-100">Formule {formule.name}</p>
              <p className="mt-2 flex items-baseline gap-2">
                <span className="text-sm text-slate-400">à partir de</span>
                <span className="font-display text-gradient text-5xl font-black" data-testid="devis-estimate-price">
                  {formule.price.toLocaleString("fr-FR")} €
                </span>
              </p>
              <div className="my-6 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
              <ul className="space-y-2.5 text-sm text-slate-300">
                {formule.features.slice(0, 3).map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                    {f}
                  </li>
                ))}
                <li className="text-slate-500">+ {formule.features.length - 3} prestations incluses</li>
              </ul>
              <div className="my-6 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
              <p className="text-sm leading-relaxed text-slate-400" data-testid="devis-estimate-note">
                {invites > 250
                  ? "Au-delà de 250 invités, nous étudions une configuration son & lumière renforcée — chiffrée dans votre devis."
                  : "Estimation indicative : votre devis final tient compte du lieu, des horaires et de vos options."}
              </p>
              <p className="mt-5 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3.5 text-xs leading-relaxed text-slate-400">
                Acompte de 30 % à la signature du devis et du contrat. Réponse garantie sous 48 h.
              </p>
            </aside>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
